import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { trackPurchase } from '../lib/metaPixel';

const PaymentSuccessPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Get parameters from URL - Mercado Pago can send params in different ways
  const searchParams = new URLSearchParams(window.location.search);
  const externalRef = searchParams.get('ref') || 
                      searchParams.get('external_reference') || 
                      searchParams.get('preference_id');

  useEffect(() => {
    if (externalRef) {
      loadOrderStatus();
    } else {
      // If no reference, still show success message but without order details
      console.warn('No order reference found in URL');
      setLoading(false);
    }
  }, [externalRef]);

  const loadOrderStatus = async () => {
    try {
      console.log(`Loading order status for: ${externalRef}`);
      const response = await axios.get(`${API}/payments/order-status/${externalRef}`);
      const status = response.data?.payment_status;

      // This page used to show "Pago exitoso" unconditionally, regardless
      // of the order's real payment_status — reachable if MercadoPago's
      // own auto_return (only fires for genuinely approved payments) sent
      // someone here, but our webhook hadn't yet updated the order's
      // status in the DB at the moment this loads. Redirecting to the
      // matching page instead means a pending/rejected order is never
      // shown a false "Pago exitoso".
      //
      // This must be a strict allowlist (status !== 'approved'), not
      // `status && status !== 'approved'` — payment_status defaults to
      // None/null on every new order (see Order model in server.py) until
      // the webhook updates it, and `null && ...` is falsy, so the old
      // truthy-check let a not-yet-confirmed order fall straight through to
      // the success branch below and fire a Purchase event for a payment
      // nothing had actually approved yet.
      if (status !== 'approved') {
        const dest = status === 'rejected' || status === 'cancelled' ? '/pago-fallido' : '/pago-pendiente';
        navigate(`${dest}?ref=${encodeURIComponent(externalRef)}`, { replace: true });
        return;
      }

      setOrder(response.data);
      console.log('Order loaded successfully:', response.data);

      // Reaching this point already means the redirect-away check above
      // confirmed payment_status === 'approved' against our own DB record
      // of the order (not just the URL the user landed on) — so this is a
      // real, backend-confirmed payment, with the real amount that was
      // charged. trackPurchase's own localStorage guard (keyed by
      // order_number) keeps a reload/back-forward on this page from
      // double-counting the same order. Isolated in its own try/catch: it
      // shares this try block with the email-trigger fallback right below
      // it, and the order already loaded successfully — a tracking error
      // here must never skip that fallback or land the user in the
      // (misleading, in this case) "couldn't load your order" catch below.
      try {
        trackPurchase({
          transactionId: response.data.order_number || externalRef,
          value: response.data.total_amount,
          currency: 'CLP',
        });
      } catch (pixelError) {
        console.error('[MetaPixel] Purchase tracking failed:', pixelError);
      }

      // Trigger email sending if payment is approved but email wasn't sent
      // This is a fallback in case webhook didn't execute
      if (response.data && response.data.payment_status === 'approved') {
        try {
          console.log('Triggering email fallback...');
          await axios.post(`${API}/payments/trigger-email/${externalRef}`);
          console.log('Email trigger sent successfully');
        } catch (emailError) {
          console.log('Email trigger failed (may have been sent already):', emailError);
        }
      }
    } catch (error) {
      console.error('Error loading order:', error);
      setError('No pudimos cargar los detalles de tu pedido, pero tu pago fue procesado correctamente.');
      // Don't throw error - still show success message
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004AAD]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        {/* Title */}
        <h1 className="text-4xl font-bold text-gray-900 mb-3 text-center">
          Pago exitoso
        </h1>
        
        <p className="text-lg text-gray-600 mb-8 text-center">
          {order ? 
            'Tu compra ha sido procesada correctamente. Hemos enviado un correo de confirmación con todos los detalles.' :
            'Tu compra ha sido procesada correctamente. Recibirás un correo de confirmación en breve con todos los detalles y pasos a seguir.'
          }
        </p>

        {/* Show error message if order couldn't be loaded */}
        {error && !order && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Order Details */}
        {order && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-[#004AAD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Resumen del Pedido
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-blue-200">
                <span className="text-gray-700 font-medium">Número de Orden:</span>
                <span className="font-mono font-bold text-[#004AAD] text-lg">#{order.order_number}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-blue-200">
                <span className="text-gray-700 font-medium">Producto:</span>
                <span className="font-semibold text-gray-900">{order.product_name || 'Tarjeta Inteligente Contact'}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-blue-200">
                <span className="text-gray-700 font-medium">Cantidad:</span>
                <span className="font-semibold text-gray-900">{order.quantity} {order.quantity === 1 ? 'unidad' : 'unidades'}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-blue-200">
                <span className="text-gray-700 font-medium">Total Pagado:</span>
                <span className="font-bold text-green-600 text-xl">${order.total_amount?.toLocaleString('es-CL')} CLP</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-blue-200">
                <span className="text-gray-700 font-medium">Correo del Comprador:</span>
                <span className="font-medium text-gray-900">{order.customer_email}</span>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-700 font-medium">Fecha:</span>
                <span className="font-medium text-gray-900">
                  {order.created_at ? new Date(order.created_at).toLocaleDateString('es-CL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Hoy'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Información de Entrega</h3>
              <p className="text-gray-700 text-sm">
                Tu pedido está en proceso. <strong>Plazo estimado de entrega: 3 a 7 días hábiles.</strong>
                <br />
                Te contactaremos ante cualquier actualización sobre tu pedido.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-[#004AAD] hover:bg-[#5170FF] text-white py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Ir a Inicio
          </button>
          
          <button
            onClick={() => window.location.href = 'mailto:contactnetwork.automatico@gmail.com?subject=Consulta sobre mi pedido'}
            className="flex-1 bg-white hover:bg-gray-50 text-[#004AAD] border-2 border-[#004AAD] py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contactar Soporte
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            <strong>CONTACT Network</strong> — Digitaliza tu networking
          </p>
        </div>
      </div>
    </div>
  );
};

// Payment Failure Page

export default PaymentSuccessPage;
